import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { ExclamationIcon, ChevronRightIcon, UserIcon, SwitchHorizontalIcon, PlusIcon, CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/solid';
import Select from 'react-select';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import { canClinicianBeAssignedToPathway } from '../../../../utils/pathwaySpecialismUtils';
import { getFriendlyStageNames, getDDSupportingStageLabel } from '../../../../utils/stageDisplayNames';
import { isSlamOrg } from '../../2_dashboard/config/patientSetupConfig';
import getScrollablePortalTarget from '../../../../utils/getScrollablePortalTarget';

/** Portal props for react-select inside this AlertDialog (scroll-lock fix). */
const DIALOG_SELECT_PORTAL_PROPS = {
    classNamePrefix: 'td-select',
    menuPortalTarget: getScrollablePortalTarget(),
    menuPosition: 'fixed',
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

/**
 * ReassignCliniciansDialog
 *
 * Business purpose:
 * - Allow providers to self-serve reassignment of PRIMARY assignees for CI, SSI, PAD, Lead, and DD (primary only).
 * - Keep DD supporting clinicians intact on a DD primary change (back-end will enforce and de-duplicate).
 * - Enforce organisation scoping by sourcing options strictly from the current organisation.
 *
 * UX model:
 * - Show a simple table of stages with "current primary" and an Edit action.
 * - Editing toggles a dropdown of clinicians; selecting the same clinician is blocked.
 * - Save builds per-stage "operations" to send to the backend callable `reassignClinicians`.
 *
 * Access Control:
 * - Org Admins and Support users have full access (can change any clinician including Lead).
 * - Lead Clinicians can access this dialog but CANNOT change the Lead role (isLeadClinicianMode=true).
 *   This enables quick MDT adjustments without requiring admin intervention, while preventing
 *   Lead Clinicians from removing themselves. Backend enforces this as defence in depth.
 *
 * Notes:
 * - Supports an optional `initialStep` prop so external entry points (e.g. the "+"
 *   button on the DD AssessmentModuleCard) can open the dialog directly on a specific
 *   wizard step instead of the summary.
 */
export default function ReassignCliniciansDialog({
  isOpen,
  onClose,
  patientId,
  patientDataModel,
  refreshPatientData,
  isLeadClinicianMode = false, // When true, disable "Change Lead Clinician" action
  initialStep = 'summary', // Which step to show when the dialog first opens
}) {
  // Derive MDT review status using the canonical hook so both modern patients
  // (with workflowConfig on doc) and legacy patients (org-based fallback) are handled.
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // Step machine: 'summary' | 'changeLead' | 'changeSupporting' | 'addDDSupporting' | 'assignSSI' | 'swapPAD' | 'success'
  const [step, setStep] = useState(initialStep || 'summary');

  const assessmentModule = patientDataModel?.assessmentModule;
  const isAutism = assessmentModule === 'adultAutism';
  // Whether this autism patient's workflow runs Behavioural Observation. Every
  // OBS-specific behaviour below (the OBS clinician slot, OBS↔DD supporting
  // linkage, the PAD Lead↔OBS swap) is gated on this: orgs that disable
  // Observation (e.g. LMP) reassign the DD supporting clinician exactly like an
  // ADHD MDT — no OBS stage involved. Absent flag → enabled, so TOC and every
  // existing autism patient are unaffected.
  const isAutismObs = isAutism && wf?.isObservationEnabled !== false;

  // Map stages to patient.clinicians fields. OBS is only relevant for autism
  // patients whose workflow actually runs the Observation stage.
  const stageFieldMap = useMemo(() => {
    const base = {
      CI: 'clinicianCI',
      SSI: 'clinicianSSI',
      DD: 'clinicianDD',
      PAD: 'clinicianPAD',
      Lead: 'clinicianLead',
    };
    if (isAutismObs) base.OBS = 'clinicianOBS';
    return base;
  }, [isAutismObs]);

  // Friendly stage names for messaging — sourced from a shared utility
  // so AssignCliniciansDialog and this dialog stay in sync.
  const friendlyStageName = useMemo(
    () => getFriendlyStageNames(assessmentModule),
    [assessmentModule]
  );

  // Derive current primary assignments for display and validation
  const currentAssignments = useMemo(() => {
    const clinicians = patientDataModel?.clinicians || {};
    const getPrimary = (field) => {
      const entry = clinicians?.[field];
      return entry ? { id: entry.clinicianId, name: entry.clinicianName } : null;
    };
    const CI = getPrimary(stageFieldMap.CI);
    const SSI = getPrimary(stageFieldMap.SSI);
    const DD = getPrimary(stageFieldMap.DD); // primary only
    const PAD = getPrimary(stageFieldMap.PAD);
    const Lead = getPrimary(stageFieldMap.Lead);
    const OBS = stageFieldMap.OBS ? getPrimary(stageFieldMap.OBS) : null;

    const ddSupportingNames = clinicians?.clinicianDD?.supportingClinicianNames || [];
    const ddSupportingIds = clinicians?.clinicianDD?.supportingClinicianIds || [];

    // Build an overview list of non-lead clinicians and the stages they are assigned to
    const leadId = Lead?.id;
    const byId = new Map(); // clinicianId -> { id, name, stageLabels: Set<string> }
    const ensure = (id, name) => {
      if (!id || id === leadId) return null;
      if (!byId.has(id)) byId.set(id, { id, name, stageLabels: new Set() });
      return byId.get(id);
    };

    // Primary stages (excluding Lead clinician if they happen to be primary).
    // OBS is included for autism patients.
    const primaryEntries = [
      ['CI', CI],
      ['SSI', SSI],
      ['DD', DD],
      ['PAD', PAD],
    ];
    if (OBS) primaryEntries.push(['OBS', OBS]);

    const ddSupportingStageLabel = getDDSupportingStageLabel(assessmentModule);

    primaryEntries.forEach(([key, entry]) => {
      if (entry && entry.id && entry.id !== leadId) {
        const rec = ensure(entry.id, entry.name);
        if (rec) rec.stageLabels.add(friendlyStageName[key]);
      }
    });

    // DD supporting (exclude lead)
    ddSupportingIds.forEach((id, idx) => {
      if (!id || id === leadId) return;
      const name = ddSupportingNames?.[idx] || 'Unnamed Clinician';
      const rec = ensure(id, name);
      if (rec) rec.stageLabels.add(ddSupportingStageLabel);
    });

    const supportingOverview = Array.from(byId.values()).map(v => ({
      id: v.id,
      name: v.name,
      stageLabels: Array.from(v.stageLabels),
    }));

    return {
      CI, SSI, DD, PAD, Lead, OBS,
      ddSupportingNames,
      ddSupportingIds,
      ddSupportingStageLabel,
      supportingOverview,
    };
  }, [patientDataModel, stageFieldMap, friendlyStageName, assessmentModule]);

  // Lead change selection
  const [leadSelection, setLeadSelection] = useState(null);
  const [leadReason, setLeadReason] = useState('');
  // Supporting replacement state
  const [supportingContext, setSupportingContext] = useState(null); // { fromId, fromName }
  const [supportingSelection, setSupportingSelection] = useState(null);
  const [supportingReason, setSupportingReason] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null); // { kind: 'lead'|'supporting'|'addDDSupporting', title, bullets[] }
  // MDT review clinician (add DD supporting) inline wizard state
  const [addSupportingSelection, setAddSupportingSelection] = useState(null);
  // SLaM: assign SSI clinician when Lead exists but SSI slot is empty
  const [assignSSISelection, setAssignSSISelection] = useState(null);

  // Options scoped to the authenticated user's organisation.
  // We use userData.organisation (from auth claims) rather than patientDataModel.organisation
  // because: (a) it's always available from the auth token, (b) Firestore security rules
  // require the querying user's org claim to match the clinician docs' org field, and
  // (c) it matches the pattern used by PatientSetupForm for consistency.
  const { userData } = useContext(GlobalStateContext);
  const [clinicianOptions, setClinicianOptions] = useState([]);

  const organisation = userData?.organisation;

  // Reset wizard state each time the dialog opens so returning to a stale
  // mid-flow step is impossible. Without this, keeping the component always-
  // mounted (for Radix Presence exit animations) would expose previous state.
  useEffect(() => {
    if (isOpen) {
      setStep(initialStep || 'summary');
      setLeadSelection(null);
      setLeadReason('');
      setSupportingContext(null);
      setSupportingSelection(null);
      setSupportingReason('');
      setSubmitError('');
      setIsSubmitting(false);
      setSuccessInfo(null);
      setAddSupportingSelection(null);
      setAssignSSISelection(null);
    }
  }, [isOpen, initialStep]);

  // Load clinicians in org (Org. Admin + Clinician) — mirrors PatientSetupForm query
  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      if (!isOpen || !organisation) return;
      try {
        const cliniciansQuery = query(
          collection(db, 'clinicians'),
          where('organisation', '==', organisation),
          where('userType', 'in', ['Org. Admin', 'Clinician'])
        );
        const snapshot = await getDocs(cliniciansQuery);
        if (cancelled) return;
        const opts = snapshot.docs
          // Non-clinical Org Admins must not be assignable. We represent this using a separate
          // boolean flag (isAssignableClinician) rather than changing userType, because userType
          // is used widely for authorization decisions.
          //
          // Backwards compatibility: missing isAssignableClinician is treated as "allowed".
          .filter(docSnap => {
            const data = docSnap.data() || {};
            if (data.accountDisabled === true) return false;
            if (data.isAssignableClinician === false) return false;
            if (!canClinicianBeAssignedToPathway(data, assessmentModule)) return false;
            return true;
          })
          .map(docSnap => {
            const data = docSnap.data() || {};
            // Prefer a readable label; fall back to clinicianFullName if title/name missing.
            const label = data.title && data.firstName && data.lastName
              ? `${data.title} ${data.firstName} ${data.lastName}`
              : (data.clinicianFullName || 'Unnamed Clinician');
            return { value: docSnap.id, label };
          });
        setClinicianOptions(opts);
      } catch (err) {
        console.error('Error loading clinician options:', err);
        if (!cancelled) setSubmitError('Failed to load clinicians. Please try again.');
      }
    };
    loadOptions();
    return () => { cancelled = true; };
  }, [isOpen, organisation, assessmentModule]);
  
  // Build operations for a lead swap:
  // - Always reassign Lead
  // - For each stage where current primary == current lead, reassign that stage to the new lead
  const buildLeadSwapOperations = useCallback((toClinicianId) => {
    const ops = [];
    const currentLeadId = currentAssignments.Lead?.id;
    if (!toClinicianId || !currentLeadId) return ops;
    // 1) Lead role
    ops.push({ stage: 'Lead', type: 'primary', toClinicianId, reason: leadReason?.trim() || undefined });
    // 2) Any stage where primary matches current lead -> move to new lead
    ['CI', 'SSI', 'DD', 'PAD'].forEach(stageKey => {
      const current = currentAssignments[stageKey];
      if (current?.id === currentLeadId) {
        ops.push({ stage: stageKey, type: 'primary', toClinicianId, reason: leadReason?.trim() || undefined });
      }
    });
    return ops;
  }, [currentAssignments, leadReason]);

  const isLeadSelectionSame = useMemo(() => {
    if (!leadSelection || !currentAssignments.Lead) return false;
    return leadSelection.value === currentAssignments.Lead.id;
  }, [leadSelection, currentAssignments]);

  const canConfirmLead = useMemo(() => {
    return !!leadSelection && !isLeadSelectionSame;
  }, [leadSelection, isLeadSelectionSame]);

  const handleConfirmLeadSwap = async () => {
    setSubmitError('');
    if (!canConfirmLead) return;
    const operations = buildLeadSwapOperations(leadSelection.value);
    if (operations.length === 0) return;
    setIsSubmitting(true);
    try {
      // Backend callable contract (to be implemented): reassignClinicians
      const reassignFn = httpsCallable(functions, 'reassignClinicians');
      const result = await reassignFn({ patientId, operations });
      if (result?.data?.success) {
        // Refresh patient data so Patient Overview reflects latest assignees
        if (typeof refreshPatientData === 'function') {
          try {
            await refreshPatientData(patientId, true);
          } catch (e) {
            // Non-fatal: UI will still reconcile next time
            console.warn('Refresh patient data failed (non-fatal):', e);
          }
        }
        // Build success info screen context
        const currentLeadName = currentAssignments.Lead?.name || 'Not assigned';
        const currentLeadId = currentAssignments.Lead?.id;
        const impacted = ['CI', 'SSI', 'DD', 'PAD'].filter(k => currentAssignments[k]?.id === currentLeadId);
        const impactedLabels = impacted.map(k => friendlyStageName[k]);
        setSuccessInfo({
          kind: 'lead',
          title: 'Assignments Updated Successfully',
          bullets: [
            `Lead changed from ${currentLeadName} to ${leadSelection?.label}.`,
            impactedLabels.length > 0
              ? `${leadSelection?.label} is now the clinician assigned for: ${impactedLabels.join(', ')}.`
              : `${leadSelection?.label} is now the Lead clinician.`
          ]
        });
        setStep('success');
      } else {
        setSubmitError(result?.data?.message || 'Failed to reassign clinicians.');
      }
    } catch (err) {
      console.error('Reassignment error:', err);
      // Friendly error message for unimplemented backend or permission issues
      const msg = err?.message?.includes('unavailable') || err?.code === 'functions/not-found'
        ? 'Service not available yet. Backend endpoint is not deployed.'
        : (err?.message || 'An unexpected error occurred.');
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Overview with badges + actions as requested
  const renderOverviewWithActions = () => {
    const hasSupporting = (currentAssignments.supportingOverview || []).length > 0;
    const currentLeadId = currentAssignments.Lead?.id;
    const leadAssignedStages = ['CI', 'SSI', 'DD', 'PAD'].filter(k => currentAssignments[k]?.id === currentLeadId);
    const leadAssignedLabels = leadAssignedStages.map(k => friendlyStageName[k]);

    return (
      <div className="space-y-4">
        {/* Lead clinician summary */}
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-violet-100 text-violet-800 border border-violet-300 shadow-sm">
              {currentAssignments.Lead?.name || 'Not assigned'}
            </span>
            <span className="text-sm text-gray-700 font-medium">is the Lead Clinician</span>
          </div>
          <div className="text-xs text-gray-600">
            {leadAssignedLabels.length > 0
              ? `Assigned to ${leadAssignedLabels.join(', ')}`
              : 'Not currently assigned to any stage'}
          </div>
        </div>

        {/* Supporting clinicians summary */}
        {hasSupporting && currentAssignments.supportingOverview.map(({ id, name, stageLabels }) => {
          const isDDOnlySupporter = stageLabels.length === 1 && stageLabels[0] === currentAssignments.ddSupportingStageLabel;
          return (
          <div key={id || name} className="space-y-1">
            <div className="flex items-center gap-1">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                {name}
              </span>
              {isDDOnlySupporter && (
                <span className="text-sm text-gray-700 font-medium">is an MDT Review Clinician</span>
              )}
            </div>
            <div className="text-xs text-gray-600">
              {stageLabels && stageLabels.length > 0
                ? `Assigned to ${stageLabels.join(', ')}`
                : 'Not currently assigned to any stage'}
            </div>
          </div>
          );
        })}

        {/* Actions panel: one row per clinician (lead first, then each supporting) */}
        <div className="bg-white border border-gray-200 rounded-md p-3">
          <h4 className="text-xs font-medium text-gray-900 mb-2">Actions</h4>
          <div className="space-y-2">
            {/* 
              Change Lead Clinician button
              
              Disabled when isLeadClinicianMode is true (user is the Lead Clinician for this patient).
              In Lead Clinician mode the only available action is "Add MDT Review Clinician" —
              they cannot swap themselves out or reassign other clinicians.
            */}
            <button
              type="button"
              onClick={() => { if (!isLeadClinicianMode) { setStep('changeLead'); setSubmitError(''); } }}
              disabled={isLeadClinicianMode}
              className={`w-full flex items-center justify-between p-2 text-left border rounded-md transition-colors ${
                isLeadClinicianMode 
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' 
                  : 'border-blue-200 hover:bg-blue-50'
              }`}
              title={isLeadClinicianMode ? 'As the Lead Clinician, you cannot reassign yourself. Please contact an administrator.' : ''}
            >
              <div className="flex items-center space-x-2">
                <SwitchHorizontalIcon className={`w-4 h-4 ${isLeadClinicianMode ? 'text-gray-400' : 'text-blue-600'}`} />
                <div>
                  <p className={`text-xs font-medium ${isLeadClinicianMode ? 'text-gray-500' : 'text-blue-900'}`}>
                    Change Lead Clinician ({currentAssignments.Lead?.name || 'Not assigned'})
                  </p>
                  <p className={`text-xs ${isLeadClinicianMode ? 'text-gray-400' : 'text-blue-700'}`}>
                    {isLeadClinicianMode 
                      ? 'Contact an administrator to change the Lead' 
                      : 'Replace with another clinician'}
                  </p>
                </div>
              </div>
              <ChevronRightIcon className={`w-4 h-4 ${isLeadClinicianMode ? 'text-gray-300' : 'text-blue-400'}`} />
            </button>

            {/* SLaM / Urban ADHD: Assign SSI clinician when Lead exists but the
                SSI slot is still empty.  Handles the reverse of the legacy
                SSI-first flow — when an admin assigned the Lead bundle first.
                For one-clinician SLaM patients the Lead bundle already includes
                SSI, so this only appears as a repair path for an empty slot.
                Only Admin/Support users can assign (gated by !isLeadClinicianMode,
                same rule as Change Lead).  Non-SLaM orgs never see this because
                isSlamOrg() returns false for WLC, TOC, Phoenix, etc. */}
            {isSlamOrg(organisation) && !currentAssignments.SSI && !isLeadClinicianMode && (
              <button
                type="button"
                onClick={() => { setAssignSSISelection(null); setSubmitError(''); setStep('assignSSI'); }}
                className="w-full flex items-center justify-between p-2 text-left border border-violet-200 rounded-md hover:bg-violet-50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <PlusIcon className="w-4 h-4 text-violet-600" />
                  <div>
                    <p className="text-xs font-medium text-violet-900">Assign SSI Clinician</p>
                    <p className="text-xs text-violet-700">
                      Assign a clinician to the {friendlyStageName.SSI}
                    </p>
                  </div>
                </div>
                <ChevronRightIcon className="w-4 h-4 text-violet-400" />
              </button>
            )}

            {/* For MDT review organisations: show add-clinician action when the supporting
                clinician slot is empty. For autism patients, this covers both the Observation
                and DD supporting roles (always assigned to the same person). The button shows
                whenever either OBS or DD supporting is unassigned so the linked invariant can
                be established in a single action. */}
            {(() => {
              const hasDDSupporting = (currentAssignments.ddSupportingIds || []).filter(Boolean).length > 0;
              const hasOBS = isAutismObs && !!currentAssignments.OBS?.id;
              const isFullyAssigned = isAutismObs ? (hasDDSupporting && hasOBS) : hasDDSupporting;
              if (!wf.isMDTReview || isFullyAssigned) return null;

              const buttonLabel = isAutismObs
                ? 'Add Observation / MDT Review Clinician'
                : 'Add MDT Review Clinician';
              const buttonDescription = isAutismObs
                ? `Add a clinician for the ${friendlyStageName.OBS} and ${friendlyStageName.DD}`
                : `Add an MDT Review Clinician for the ${friendlyStageName.DD}`;
              return (
                <button
                  type="button"
                  onClick={() => { setAddSupportingSelection(null); setSubmitError(''); setStep('addDDSupporting'); }}
                  className="w-full flex items-center justify-between p-2 text-left border border-violet-200 rounded-md hover:bg-violet-50 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <PlusIcon className="w-4 h-4 text-violet-600" />
                    <div>
                      <p className="text-xs font-medium text-violet-900">{buttonLabel}</p>
                      <p className="text-xs text-violet-700">{buttonDescription}</p>
                    </div>
                  </div>
                  <ChevronRightIcon className="w-4 h-4 text-violet-400" />
                </button>
              );
            })()}

            {/* Swap Feedback Session clinician (Autism only).
                When both Lead and OBS clinicians are assigned, allow PAD to be swapped
                between them. This supports the common scenario where the OBS clinician
                takes over the Feedback Session from the Lead, or vice versa.
                Disabled in Lead Clinician mode (matches Change Lead restrictions). */}
            {(() => {
              if (!isAutismObs) return null;
              const leadId = currentAssignments.Lead?.id;
              const obsId = currentAssignments.OBS?.id;
              const padId = currentAssignments.PAD?.id;
              if (!leadId || !obsId || !padId) return null;
              const padIsOnLead = padId === leadId;
              const padIsOnObs = padId === obsId;
              if (!padIsOnLead && !padIsOnObs) return null;
              const currentHolder = padIsOnLead ? currentAssignments.Lead?.name : currentAssignments.OBS?.name;
              const targetRole = padIsOnLead ? 'Observation clinician' : 'Lead clinician';
              return (
                <button
                  type="button"
                  onClick={() => { if (!isLeadClinicianMode) { setSubmitError(''); setStep('swapPAD'); } }}
                  disabled={isLeadClinicianMode}
                  className={`w-full flex items-center justify-between p-2 text-left border rounded-md transition-colors ${
                    isLeadClinicianMode
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                      : 'border-amber-200 hover:bg-amber-50'
                  }`}
                  title={isLeadClinicianMode ? 'Contact an administrator to change the Feedback Session clinician.' : ''}
                >
                  <div className="flex items-center space-x-2">
                    <SwitchHorizontalIcon className={`w-4 h-4 ${isLeadClinicianMode ? 'text-gray-400' : 'text-amber-600'}`} />
                    <div>
                      <p className={`text-xs font-medium ${isLeadClinicianMode ? 'text-gray-500' : 'text-amber-900'}`}>
                        Swap {friendlyStageName.PAD} Clinician
                      </p>
                      <p className={`text-xs ${isLeadClinicianMode ? 'text-gray-400' : 'text-amber-700'}`}>
                        Currently: {currentHolder} — swap to {targetRole}
                      </p>
                    </div>
                  </div>
                  <ChevronRightIcon className={`w-4 h-4 ${isLeadClinicianMode ? 'text-gray-300' : 'text-amber-400'}`} />
                </button>
              );
            })()}

            {/* Reassign individual supporting clinicians.
                In Lead Clinician mode, only DD supporting clinicians (MDT review participants)
                can be swapped — the Lead cannot reassign other primary clinicians. */}
            {(currentAssignments.supportingOverview || []).map(({ id, name, stageLabels }) => {
              const isDDOnlySupporter = stageLabels.length === 1
                && stageLabels[0] === currentAssignments.ddSupportingStageLabel;
              if (isLeadClinicianMode && !isDDOnlySupporter) return null;
              return (
                <button
                  key={`${id || name}`}
                  type="button"
                  className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setSupportingContext({
                      fromId: id,
                      fromName: name
                    });
                    setSupportingSelection(null);
                    setSupportingReason('');
                    setSubmitError('');
                    setStep('changeSupporting');
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <UserIcon className="w-4 h-4 text-gray-600" />
                    <div>
                      <p className="text-xs font-medium text-gray-900">Reassign Clinician ({name})</p>
                      <p className="text-xs text-gray-700">Replace with another clinician</p>
                    </div>
                  </div>
                  <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderChangeSupporting = () => {
    if (!supportingContext) return null;
    const { fromId, fromName } = supportingContext;
    // Determine if this clinician is currently a DD supporting clinician
    const willBeDdSupporting = (currentAssignments.ddSupportingIds || []).includes(fromId);

    // For autism patients, replacing any supporting clinician always updates both
    // OBS and DD supporting (backend enforces the linked invariant). The exclusion
    // set and UI labels reflect this: the replacement will always become the sole
    // Observation + MDT review clinician alongside the Lead.
    const isAutismSupportingReplace = isAutismObs;

    // Exclusions:
    // - Always exclude the 'from' clinician
    // - For autism: always exclude Lead and existing DD supporters (the replacement
    //   takes over the entire supporting clinician role)
    // - For ADHD: only exclude Lead/DD supporters when replacing a DD supporter
    const excludeIds = new Set([
      fromId,
      ...((willBeDdSupporting || isAutismSupportingReplace)
        ? [currentAssignments.Lead?.id, ...(currentAssignments.ddSupportingIds || [])]
        : [])
    ].filter(Boolean));
    const filteredOptions = clinicianOptions.filter(opt => !excludeIds.has(opt.value));

    // Determine impacted primary stages for fromId.
    // For autism, OBS is always impacted since the backend enforces the OBS+DD linkage.
    const impactedPrimary = ['CI', 'SSI', 'PAD'].filter(k => currentAssignments[k]?.id === fromId);
    if (isAutismSupportingReplace && !impactedPrimary.includes('OBS')) {
      impactedPrimary.push('OBS');
    }
    const impactedLabels = impactedPrimary.map(k => friendlyStageName[k]);
    const canConfirm = !!supportingSelection;

    const handleConfirm = async () => {
      setSubmitError('');
      if (!canConfirm) return;
      setIsSubmitting(true);
      try {
        const reassignFn = httpsCallable(functions, 'reassignClinicians');
        const result = await reassignFn({
          patientId,
          operations: [{
            type: 'supportingReplace',
            fromClinicianId: fromId,
            toClinicianId: supportingSelection.value,
            reason: supportingReason?.trim() || undefined
          }]
        });
        if (result?.data?.success) {
          if (typeof refreshPatientData === 'function') {
            try { await refreshPatientData(patientId, true); } catch (e) {}
          }
          // Build success info for supporting replace.
          // For autism patients, the replacement always covers both OBS and DD supporting.
          const bullets = [];
          bullets.push(`Replaced ${fromName} with ${supportingSelection?.label}.`);
          if (isAutismSupportingReplace) {
            bullets.push(`${supportingSelection?.label} is now the Observation and MDT Review Clinician, assigned to ${friendlyStageName.OBS} and the ${friendlyStageName.DD}.`);
          } else {
            if (willBeDdSupporting) {
              bullets.push(`${supportingSelection?.label} is now an MDT Review Clinician for the ${friendlyStageName.DD}.`);
            }
          }
          // Show any additional primary stages impacted (CI/SSI/PAD); exclude OBS
          // since it's already covered by the autism-specific bullet above.
          const additionalLabels = isAutismSupportingReplace
            ? impactedLabels.filter(l => l !== friendlyStageName.OBS)
            : impactedLabels;
          if (additionalLabels.length > 0) {
            bullets.push(`${supportingSelection?.label} is now the clinician assigned for: ${additionalLabels.join(', ')}.`);
          }
          setSuccessInfo({
            kind: 'supporting',
            title: 'Assignments Updated Successfully',
            bullets
          });
          setStep('success');
        } else {
          setSubmitError(result?.data?.message || 'Failed to update clinician assignment.');
        }
      } catch (err) {
        console.error('replaceSupportingClinician error:', err);
        const msg = err?.message?.includes('unavailable') || err?.code === 'functions/not-found'
          ? 'Service not available yet. Backend endpoint is not deployed.'
          : (err?.message || 'An unexpected error occurred.');
        setSubmitError(msg);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Reassign Clinician ({fromName})
          </h3>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Select replacement <span className="text-red-500">*</span>
          </label>
          <Select
            {...DIALOG_SELECT_PORTAL_PROPS}
            isMulti
            options={filteredOptions}
            value={supportingSelection ? [supportingSelection] : []}
            onChange={(selected) => {
              const newSelection = (selected && selected.length > 0) ? selected[selected.length - 1] : null;
              setSupportingSelection(newSelection);
            }}
            placeholder="Choose clinician"
            isSearchable
            closeMenuOnSelect={true}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault();
              }
            }}
            className="text-left"
          />
        </div>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            supportingSelection ? 'max-h-64 mt-2 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-3 rounded-md border bg-amber-50 border-amber-200">
            <h4 className="text-xs font-semibold mb-2 text-amber-900">What this change means</h4>
            <ul className="text-xs space-y-1 text-amber-700">
              {isAutismSupportingReplace ? (
                <li className="flex items-start space-x-1">
                  <span className="text-xs mt-0.5">•</span>
                  <span>
                    The new clinician will become the <span className="font-semibold">Observation and MDT Review Clinician</span>, assigned to the {friendlyStageName.OBS} and {friendlyStageName.DD}.
                  </span>
                </li>
              ) : (
                willBeDdSupporting && (
                  <li className="flex items-start space-x-1">
                    <span className="text-xs mt-0.5">•</span>
                    <span>
                      The new clinician will become an <span className="font-semibold">MDT Review Clinician for the {friendlyStageName.DD}</span>.
                    </span>
                  </li>
                )
              )}
              {(() => {
                const extraLabels = isAutismSupportingReplace
                  ? impactedLabels.filter(l => l !== friendlyStageName.OBS)
                  : impactedLabels;
                return extraLabels.length > 0 ? (
                  <li className="flex items-start space-x-1">
                    <span className="text-xs mt-0.5">•</span>
                    <span>
                      The new clinician will become the <span className="font-semibold">primary assignee</span> for: {extraLabels.join(', ')}.
                    </span>
                  </li>
                ) : null;
              })()}
            </ul>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Reason (optional)</label>
          <input
            type="text"
            placeholder="e.g., Rota change, cover needed"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
            value={supportingReason}
            onChange={(e) => setSupportingReason(e.target.value)}
          />
        </div>
        {submitError && (
          <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
            {submitError}
          </div>
        )}
        <div className="mt-2 flex justify-between">
          <button
            type="button"
            onClick={() => { setStep('summary'); setSupportingContext(null); setSupportingSelection(null); setSupportingReason(''); setSubmitError(''); }}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            disabled={isSubmitting}
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm || isSubmitting}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
              !canConfirm || isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Saving...</span>
              </div>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    );
  };

  // Add MDT Review Clinician — inline wizard step that replaces the former
  // standalone AddDDSupportingDialog. Filters the already-loaded clinicianOptions
  // to exclude anyone already assigned to DD (primary or supporting).
  const renderAddDDSupporting = () => {
    const excludeIds = new Set();
    if (currentAssignments.DD?.id) excludeIds.add(currentAssignments.DD.id);
    (currentAssignments.ddSupportingIds || []).forEach(id => { if (id) excludeIds.add(id); });
    const filteredOptions = clinicianOptions.filter(opt => !excludeIds.has(opt.value));
    const noEligible = filteredOptions.length === 0 && clinicianOptions.length > 0;
    const canConfirm = !!addSupportingSelection;

    const handleConfirmAddSupporting = async () => {
      setSubmitError('');
      if (!canConfirm) return;
      setIsSubmitting(true);
      try {
        const reassignFn = httpsCallable(functions, 'reassignClinicians');
        const result = await reassignFn({
          patientId,
          operations: [{ type: 'addDDSupporting', toClinicianId: addSupportingSelection.value }],
        });
        if (result?.data?.success) {
          if (typeof refreshPatientData === 'function') {
            try { await refreshPatientData(patientId, true); } catch (e) {
              console.warn('Refresh patient data failed (non-fatal):', e);
            }
          }
          setSuccessInfo({
            kind: 'addDDSupporting',
            title: isAutismObs ? 'Observation / MDT Review Clinician Added' : 'MDT Review Clinician Added',
            bullets: isAutismObs
              ? [
                  `${addSupportingSelection.label} has been assigned to the ${friendlyStageName.OBS} and added as an MDT Review Clinician for the ${friendlyStageName.DD}.`,
                  `They can now conduct the ${friendlyStageName.OBS} and contribute to this patient's ${friendlyStageName.DD}.`,
                ]
              : [
                  `${addSupportingSelection.label} has been added as an MDT Review Clinician for the ${friendlyStageName.DD}.`,
                  `They can now view and contribute to this patient's ${friendlyStageName.DD}.`,
                ],
          });
          setStep('success');
        } else {
          setSubmitError(result?.data?.message || 'Failed to add MDT review clinician.');
        }
      } catch (err) {
        console.error('addDDSupporting error:', err);
        const msg = err?.message?.includes('unavailable') || err?.code === 'functions/not-found'
          ? 'Service not available yet. Backend endpoint is not deployed.'
          : err?.message?.includes('Only one supporting clinician')
            ? 'An MDT review clinician has already been added by another user. Please close and try again.'
            : (err?.message || 'An unexpected error occurred.');
        setSubmitError(msg);
      } finally {
        setIsSubmitting(false);
      }
    };

    const addTitle = isAutismObs ? 'Add Observation / MDT Review Clinician' : 'Add MDT Review Clinician';
    const addDescription = isAutismObs
      ? `Select a clinician to conduct the ${friendlyStageName.OBS} and participate in the ${friendlyStageName.DD} alongside the lead clinician.`
      : `Select a clinician to participate in the ${friendlyStageName.DD} alongside the lead clinician.`;

    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">
            {addTitle}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            {addDescription}
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Clinician <span className="text-red-500">*</span>
          </label>
          {noEligible ? (
            <div className="p-3 rounded-md border bg-gray-50 border-gray-200">
              <p className="text-xs text-gray-600">
                No eligible clinicians available. All clinicians in your organisation are already assigned to this module.
              </p>
            </div>
          ) : (
            <Select
              {...DIALOG_SELECT_PORTAL_PROPS}
              options={filteredOptions}
              value={addSupportingSelection}
              onChange={(selected) => setAddSupportingSelection(selected)}
              placeholder="Choose clinician"
              isSearchable
              isClearable
              className="text-left"
            />
          )}
        </div>

        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            addSupportingSelection ? 'max-h-64 mt-2 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-3 rounded-md border bg-amber-50 border-amber-200">
            <h4 className="text-xs font-semibold mb-2 text-amber-900">What this means</h4>
            <ul className="text-xs space-y-1 text-amber-700">
              {isAutismObs && (
                <li className="flex items-start space-x-1">
                  <span className="text-xs mt-0.5">•</span>
                  <span>
                    <span className="font-semibold">{addSupportingSelection?.label}</span> will be assigned to the <span className="font-semibold">{friendlyStageName.OBS}</span>.
                  </span>
                </li>
              )}
              <li className="flex items-start space-x-1">
                <span className="text-xs mt-0.5">•</span>
                <span>
                  <span className="font-semibold">{addSupportingSelection?.label}</span> will be added as an MDT Review Clinician for the <span className="font-semibold">{friendlyStageName.DD}</span>.
                </span>
              </li>
              <li className="flex items-start space-x-1">
                <span className="text-xs mt-0.5">•</span>
                <span>
                  They will be able to {isAutismObs ? `conduct the ${friendlyStageName.OBS} and ` : ''}view and contribute to the {friendlyStageName.DD} alongside the lead clinician.
                </span>
              </li>
            </ul>
          </div>
        </div>

        {submitError && (
          <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
            {submitError}
          </div>
        )}

        <div className="mt-2 flex justify-between">
          <button
            type="button"
            onClick={() => { setStep('summary'); setAddSupportingSelection(null); setSubmitError(''); }}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            disabled={isSubmitting}
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleConfirmAddSupporting}
            disabled={!canConfirm || isSubmitting}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
              !canConfirm || isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Adding...</span>
              </div>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    );
  };

  // Assign SSI Clinician — SLaM / Urban ADHD only.
  // Shown when the Lead bundle has been assigned first (Lead + CI + DD + PAD)
  // but the SSI slot is still empty.  Follows the same inline-wizard pattern
  // as renderAddDDSupporting: a clinician picker, a confirmation button, and
  // a single backend operation via reassignClinicians.  Non-SLaM orgs never
  // reach this step because the action button is gated on isSlamOrg().
  const renderAssignSSI = () => {
    const canConfirm = !!assignSSISelection;

    const handleConfirmAssignSSI = async () => {
      setSubmitError('');
      if (!canConfirm) return;
      setIsSubmitting(true);
      try {
        const reassignFn = httpsCallable(functions, 'reassignClinicians');
        const result = await reassignFn({
          patientId,
          operations: [{ stage: 'SSI', type: 'primary', toClinicianId: assignSSISelection.value }],
        });
        if (result?.data?.success) {
          if (typeof refreshPatientData === 'function') {
            try { await refreshPatientData(patientId, true); } catch (e) {
              console.warn('Refresh patient data failed (non-fatal):', e);
            }
          }
          setSuccessInfo({
            kind: 'assignSSI',
            title: 'SSI Clinician Assigned',
            bullets: [
              `${assignSSISelection.label} has been assigned to the ${friendlyStageName.SSI}.`,
              // Legacy SLaM patients run the SSI as a separate first appointment;
              // one-clinician patients run it inside the Lead's single appointment,
              // so do not describe it as "the first appointment" for them.
              wf.isSSIFirst
                ? 'This is typically the first appointment. The Lead clinician remains unchanged.'
                : 'The Lead clinician and all other stage assignments remain unchanged.',
            ],
          });
          setStep('success');
        } else {
          setSubmitError(result?.data?.message || 'Failed to assign SSI clinician.');
        }
      } catch (err) {
        console.error('assignSSI error:', err);
        const msg = err?.message?.includes('unavailable') || err?.code === 'functions/not-found'
          ? 'Service not available yet. Backend endpoint is not deployed.'
          : (err?.message || 'An unexpected error occurred.');
        setSubmitError(msg);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Assign SSI Clinician
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Select the clinician who will conduct the {friendlyStageName.SSI}.
            {/* Legacy SSI-first patients expect a second clinician here; in the
                one-clinician workflow the Lead normally holds this slot too, and
                this step only exists to repair an empty SSI slot. */}
            {wf.isSSIFirst
              ? ' This is typically a separate clinician from the Lead.'
              : ' In the one-clinician workflow this is normally the Lead clinician, who runs the interview in the same appointment.'}
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            SSI Clinician <span className="text-red-500">*</span>
          </label>
          <Select
            {...DIALOG_SELECT_PORTAL_PROPS}
            options={clinicianOptions}
            value={assignSSISelection}
            onChange={(selected) => setAssignSSISelection(selected)}
            placeholder="Choose clinician"
            isSearchable
            isClearable
            className="text-left"
          />
        </div>

        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            assignSSISelection ? 'max-h-64 mt-2 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-3 rounded-md border bg-amber-50 border-amber-200">
            <h4 className="text-xs font-semibold mb-2 text-amber-900">What this means</h4>
            <ul className="text-xs space-y-1 text-amber-700">
              <li className="flex items-start space-x-1">
                <span className="text-xs mt-0.5">•</span>
                <span>
                  <span className="font-semibold">{assignSSISelection?.label}</span> will be assigned to the <span className="font-semibold">{friendlyStageName.SSI}</span>.
                </span>
              </li>
              <li className="flex items-start space-x-1">
                <span className="text-xs mt-0.5">•</span>
                <span>
                  The Lead clinician and all other stage assignments will remain unchanged.
                </span>
              </li>
            </ul>
          </div>
        </div>

        {submitError && (
          <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
            {submitError}
          </div>
        )}

        <div className="mt-2 flex justify-between">
          <button
            type="button"
            onClick={() => { setStep('summary'); setAssignSSISelection(null); setSubmitError(''); }}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            disabled={isSubmitting}
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleConfirmAssignSSI}
            disabled={!canConfirm || isSubmitting}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
              !canConfirm || isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Assigning...</span>
              </div>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    );
  };

  // Swap PAD clinician between Lead and OBS (Autism only).
  // Uses existing backend primary reassignment — no new operation type needed.
  const renderSwapPAD = () => {
    const leadId = currentAssignments.Lead?.id;
    const obsId = currentAssignments.OBS?.id;
    const padId = currentAssignments.PAD?.id;
    const padIsOnLead = padId === leadId;
    const targetId = padIsOnLead ? obsId : leadId;
    const targetName = padIsOnLead ? currentAssignments.OBS?.name : currentAssignments.Lead?.name;
    const currentName = padIsOnLead ? currentAssignments.Lead?.name : currentAssignments.OBS?.name;
    const targetRole = padIsOnLead ? `${friendlyStageName.OBS} clinician` : 'Lead clinician';

    const handleConfirmSwap = async () => {
      setSubmitError('');
      setIsSubmitting(true);
      try {
        const reassignFn = httpsCallable(functions, 'reassignClinicians');
        const result = await reassignFn({
          patientId,
          operations: [{ stage: 'PAD', type: 'primary', toClinicianId: targetId }],
        });
        if (result?.data?.success) {
          if (typeof refreshPatientData === 'function') {
            try { await refreshPatientData(patientId, true); } catch (e) {}
          }
          setSuccessInfo({
            kind: 'swapPAD',
            title: `${friendlyStageName.PAD} Clinician Updated`,
            bullets: [
              `${friendlyStageName.PAD} reassigned from ${currentName} to ${targetName}.`,
              `${targetName} (${targetRole}) will now conduct the ${friendlyStageName.PAD}.`,
            ],
          });
          setStep('success');
        } else {
          setSubmitError(result?.data?.message || `Failed to swap ${friendlyStageName.PAD} clinician.`);
        }
      } catch (err) {
        console.error('swapPAD error:', err);
        const msg = err?.message?.includes('unavailable') || err?.code === 'functions/not-found'
          ? 'Service not available yet. Backend endpoint is not deployed.'
          : (err?.message || 'An unexpected error occurred.');
        setSubmitError(msg);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Swap {friendlyStageName.PAD} Clinician
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            The {friendlyStageName.PAD} is currently assigned to {currentName}. Confirm to reassign it to {targetName} ({targetRole}).
          </p>
        </div>

        <div className="p-3 rounded-md border bg-amber-50 border-amber-200">
          <h4 className="text-xs font-semibold mb-2 text-amber-900">What this change means</h4>
          <ul className="text-xs space-y-1 text-amber-700">
            <li className="flex items-start space-x-1">
              <span className="text-xs mt-0.5">•</span>
              <span>
                <span className="font-semibold">{targetName}</span> will become the clinician assigned to the <span className="font-semibold">{friendlyStageName.PAD}</span>.
              </span>
            </li>
            <li className="flex items-start space-x-1">
              <span className="text-xs mt-0.5">•</span>
              <span>
                <span className="font-semibold">{currentName}</span> will no longer be assigned to this stage.
              </span>
            </li>
            <li className="flex items-start space-x-1">
              <span className="text-xs mt-0.5">•</span>
              <span>
                This can be reversed by swapping back from the summary screen.
              </span>
            </li>
          </ul>
        </div>

        {submitError && (
          <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
            {submitError}
          </div>
        )}

        <div className="mt-2 flex justify-between">
          <button
            type="button"
            onClick={() => { setStep('summary'); setSubmitError(''); }}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            disabled={isSubmitting}
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleConfirmSwap}
            disabled={isSubmitting}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
              isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Saving...</span>
              </div>
            ) : (
              'Confirm Swap'
            )}
          </button>
        </div>
      </div>
    );
  };

  // Success screen
  const renderSuccess = () => {
    const title = successInfo?.title || 'Assignments Updated Successfully';
    const bullets = successInfo?.bullets || [];
    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="border rounded-md p-3 bg-green-50 border-green-200">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <CheckCircleSolid className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-xs font-semibold mb-2 text-green-800">Change applied</h4>
              <ul className="text-xs space-y-1 text-green-700 list-disc pl-4">
                {bullets.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => onClose()}
            className="px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors bg-violet-600 hover:bg-violet-700"
          >
            Finish
          </button>
        </div>
      </div>
    );
  };

  // Change Lead screen
  const renderChangeLead = () => {
    const filteredOptions = clinicianOptions.filter(opt => opt.value !== currentAssignments.Lead?.id);

    // Determine impacted stages (where current primary matches the current lead)
    const currentLeadId = currentAssignments.Lead?.id;
    const impacted = ['CI', 'SSI', 'DD', 'PAD'].filter(k => currentAssignments[k]?.id === currentLeadId);
    const impactedLabels = impacted.map(k => friendlyStageName[k]);

    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Change Lead Clinician from {currentAssignments.Lead?.name || 'Not assigned'}
          </h3>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Select new lead <span className="text-red-500">*</span>
          </label>
          <Select
            {...DIALOG_SELECT_PORTAL_PROPS}
            isMulti
            options={filteredOptions}
            value={leadSelection ? [leadSelection] : []}
            onChange={(selected) => {
              const newSelection = (selected && selected.length > 0) ? selected[selected.length - 1] : null;
              setLeadSelection(newSelection);
            }}
            placeholder="Choose clinician"
            isSearchable
            closeMenuOnSelect={true}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault();
              }
            }}
            className="text-left"
          />
          {leadSelection && isLeadSelectionSame && (
            <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center">
              <ExclamationIcon className="w-3.5 h-3.5 mr-1" />
              Please choose a clinician different from the current lead.
            </div>
          )}
        </div>

        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            leadSelection && !isLeadSelectionSame ? 'max-h-64 mt-2 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-3 rounded-md border bg-amber-50 border-amber-200">
            <h4 className="text-xs font-semibold mb-2 text-amber-900">What this change means</h4>
            <ul className="text-xs space-y-1 text-amber-700">
              <li className="flex items-start space-x-1">
                <span className="text-xs mt-0.5">•</span>
                <span>
                  Lead will change from <span className="font-semibold">{currentAssignments.Lead?.name}</span> to <span className="font-semibold">{leadSelection?.label}</span>.
                </span>
              </li>
              <li className="flex items-start space-x-1">
                <span className="text-xs mt-0.5">•</span>
                <span>
                  <span className="font-semibold">{leadSelection?.label}</span> will become the primary assignee for: {impactedLabels.length > 0 ? impactedLabels.join(', ') : 'No stages (lead only)'}.
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Reason (optional)</label>
          <input
            type="text"
            placeholder="e.g., Sickness cover, capacity, rota change"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
            value={leadReason}
            onChange={(e) => setLeadReason(e.target.value)}
          />
        </div>

        {submitError && (
          <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
            {submitError}
          </div>
        )}

        <div className="mt-2 flex justify-between">
          <button
            type="button"
            onClick={() => { setStep('summary'); setLeadSelection(null); setLeadReason(''); setSubmitError(''); }}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            disabled={isSubmitting}
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleConfirmLeadSwap}
            disabled={!canConfirmLead || isSubmitting}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
              !canConfirmLead || isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Saving...</span>
              </div>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialog.Portal>
      <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <AlertDialog.Title className="text-xl font-semibold mb-4">
            Reassign Clinicians
          </AlertDialog.Title>
          <VisuallyHidden.Root>
            <AlertDialog.Description>
              Review current assignments and perform a targeted change.
            </AlertDialog.Description>
          </VisuallyHidden.Root>

          <div className="text-left">
            {step === 'summary' && renderOverviewWithActions()}
            {step === 'changeLead' && renderChangeLead()}
            {step === 'changeSupporting' && renderChangeSupporting()}
            {step === 'addDDSupporting' && renderAddDDSupporting()}
            {step === 'assignSSI' && renderAssignSSI()}
            {step === 'swapPAD' && renderSwapPAD()}
            {step === 'success' && renderSuccess()}
          </div>

          {step === 'summary' && (
            <div className="mt-6 flex justify-start gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-semibold rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
            </div>
          )}
        </AlertDialog.Content>
      </AlertDialog.Portal>

    </AlertDialog.Root>
  );
}

